/*
 * /board-game-bank — the poker engine: blinds, betting rounds, side pots.
 *
 * Pure module, no DOM. It tracks the CHIPS of a no-limit hold'em / omaha hand
 * played with real cards on a real table: who has to act, how much to call,
 * what a legal raise is, and — the part home games get wrong most often —
 * how the pot splits when somebody is all-in.
 *
 * Rules implemented (and pinned by tests/js/board-game-bank-poker.test.mjs):
 *   · blinds: heads-up the button posts the small blind and acts first before
 *     the flop, last after it; three or more — SB left of the button, BB next,
 *     first to act left of the BB; after the flop, first active left of the
 *     button. A blind short of chips posts what it has and is all-in; the
 *     amount to call stays the full big blind.
 *   · antes are dead money: they count toward a player's share of the pot
 *     (and so toward side pots) but not toward the bet in front of them.
 *   · no-limit raising: a raise is at least the size of the previous bet or
 *     raise in the round; a player may go all-in for less. An all-in raise
 *     that is SHORT of a full raise does not reopen the betting for players
 *     who have already acted — they may only call or fold.
 *   · an uncalled bet is returned to its owner when the round closes.
 *   · pots are built from what each player put in over the whole hand:
 *     one pot per distinct all-in level, each contested by the players who
 *     are still in and put in at least that much; chips of folded players
 *     stay in the pots they reached.
 *   · a split pot is divided in units of the smallest chip; the odd chips go
 *     one by one to the winners in seat order starting left of the button.
 *
 * The engine never decides who has the best cards — the people at the table
 * do. At showdown the page asks «who won this pot?» for each pot, offering
 * only the players who are eligible for it.
 */

/* ── blinds structure, chips ──────────────────────────────────────────────── */

/* A classic home-tournament ladder, expressed as multiples of the first big
   blind: 1, 1.5, 2.5, 5, 7.5, 10, 15, 20, 30, 40, 50, 75, 100. */
const LADDER = [1, 1.5, 2.5, 5, 7.5, 10, 15, 20, 30, 40, 50, 75, 100];

export function defaultLevels(bb, minutes, ante) {
    const b = bb > 0 ? bb : 20;
    const min = minutes > 0 ? minutes : 15;
    return LADDER.map((k) => {
        const big = roundChip(b * k, b >= 10 ? 5 : 1);
        const small = roundChip(big / 2, 1);
        return { sb: small, bb: big, ante: ante ? Math.max(0, roundChip(big / 10, 1)) : 0, min };
    });
}

function roundChip(v, step) {
    return Math.max(step, Math.round(v / step) * step);
}

/* A standard home set: white 5, red 25, green 100, black 500, purple 1000. */
export const DEFAULT_DENOMS = [
    { v: 5, c: '#f1f5f9' },
    { v: 25, c: '#dc2626' },
    { v: 100, c: '#16a34a' },
    { v: 500, c: '#1f2937' },
    { v: 1000, c: '#7c3aed' },
];

/** The smallest chip — the unit bets are made in and odd chips are split by. */
export function unitOf(denoms) {
    let u = 0;
    for (const d of denoms || []) if (d && d.v > 0 && (!u || d.v < u)) u = d.v;
    return u || 1;
}

/**
 * How to make `amount` out of chips: greedy from the largest, which is the
 * way chips are counted out at a table. Returns [{v, c, n}] (largest first)
 * and `rest` — what cannot be made (amount not a multiple of the unit).
 */
export function breakdown(amount, denoms) {
    const list = (denoms || []).filter((d) => d && d.v > 0).slice().sort((a, b) => b.v - a.v);
    let left = Math.max(0, Math.floor(amount));
    const out = [];
    for (const d of list) {
        const n = Math.floor(left / d.v);
        if (n > 0) { out.push({ v: d.v, c: d.c, n }); left -= n * d.v; }
    }
    return { chips: out, rest: left };
}

/**
 * A starting stack counted out so that everyone has small chips to bet with:
 * a quarter of the stack is kept in the smallest two denominations, the rest
 * greedy. Greedy alone gives a 1 000 stack as one purple chip — nobody can
 * post a blind with that.
 */
export function startingChips(amount, denoms) {
    const list = (denoms || []).filter((d) => d && d.v > 0).slice().sort((a, b) => a.v - b.v);
    if (!list.length) return { chips: [], rest: amount };
    let small = Math.floor(amount / 4);
    const out = new Map();
    const add = (d, n) => { if (n > 0) out.set(d.v, { v: d.v, c: d.c, n: (out.get(d.v) ? out.get(d.v).n : 0) + n }); };
    const lowTwo = list.slice(0, 2).reverse();          // the bigger of the two first
    for (const d of lowTwo) {
        const share = d === lowTwo[0] && lowTwo.length > 1 ? Math.floor(small / 2) : small;
        const n = Math.floor(share / d.v);
        add(d, n);
        small -= n * d.v;
    }
    let left = amount;
    for (const [, x] of out) left -= x.v * x.n;
    const rest = breakdown(left, list);
    for (const x of rest.chips) add(x, x.n);
    return { chips: Array.from(out.values()).sort((a, b) => b.v - a.v), rest: rest.rest };
}

/* ── blinds timer (wall clock, survives a reload) ─────────────────────────── */

/**
 * timer: {lvl, on, t0, acc} — `acc` ms already spent in the level before
 * `t0` (when the clock last started). Returns where the clock is NOW,
 * rolling over levels the way they would have while the tab was closed.
 */
export function timerAt(timer, levels, now) {
    const lv = levels && levels.length ? levels : [{ min: 15 }];
    let lvl = Math.min(Math.max(0, timer.lvl | 0), lv.length - 1);
    let spent = (timer.acc || 0) + (timer.on ? Math.max(0, now - (timer.t0 || 0)) : 0);
    let len = lv[lvl].min * 60000;
    while (spent >= len && lvl < lv.length - 1) {
        spent -= len;
        lvl++;
        len = lv[lvl].min * 60000;
    }
    const last = lvl === lv.length - 1;
    return { lvl, spent, remain: Math.max(0, len - spent), last, over: last && spent >= len };
}

/** Fold the rolled-over levels into the stored state (after a level-up). */
export function timerCommit(timer, levels, now) {
    const at = timerAt(timer, levels, now);
    return { lvl: at.lvl, on: timer.on && !at.over, t0: timer.on ? now : 0, acc: at.spent };
}

export function timerStart(timer, now) { return timer.on ? timer : { lvl: timer.lvl | 0, on: true, t0: now, acc: timer.acc || 0 }; }

export function timerPause(timer, levels, now) {
    const at = timerAt(timer, levels, now);
    return { lvl: at.lvl, on: false, t0: 0, acc: at.spent };
}

export function timerJump(timer, levels, now, dir) {
    const at = timerAt(timer, levels, now);
    const lvl = Math.min(Math.max(0, at.lvl + dir), Math.max(0, (levels || []).length - 1));
    return { lvl, on: timer.on, t0: timer.on ? now : 0, acc: 0 };
}

/* ── the hand ─────────────────────────────────────────────────────────────── */

function nextIn(order, from, pred) {
    const n = order.length;
    const i = order.indexOf(from);
    for (let k = 1; k <= n; k++) {
        const p = order[(i + k + n) % n];
        if (!pred || pred(p)) return p;
    }
    return null;
}

/**
 * Start a hand.
 *   seats   pids in seat order (the table order, all players at the table)
 *   stacks  {pid: chips}
 *   prev    the previous button (null for the first hand)
 *   blinds  {sb, bb, ante}
 *   button  optional: force the dealer (host moved the button by hand)
 * Returns {ok, hand} or {ok:false, err}.
 */
export function startHand(seats, stacks, prev, blinds, n, button) {
    const inHand = seats.filter((p) => (stacks[p] || 0) > 0);
    if (inHand.length < 2) return { ok: false, err: 'players' };
    const sb = Math.max(0, blinds.sb | 0), bb = Math.max(1, blinds.bb | 0), ante = Math.max(0, blinds.ante | 0);
    let btn;
    if (button && inHand.indexOf(button) >= 0) btn = button;
    else if (prev == null) btn = inHand[0];
    else {
        // the button moves to the next player at the table who still has chips
        const all = seats.slice();
        if (all.indexOf(prev) < 0) btn = inHand[0];
        else btn = nextIn(all, prev, (p) => inHand.indexOf(p) >= 0);
    }
    const h = {
        n: n || 1,
        order: inHand,
        button: btn,
        sb, bb, ante,
        street: 0,
        s: {},
        cur: bb,
        minR: bb,
        rseq: 0,
        need: [],
        toAct: null,
        log: [],
        refunds: {},
        done: false,
        winner: null,
    };
    for (const p of inHand) h.s[p] = { stack: stacks[p], bet: 0, total: 0, fold: false, allin: false, seq: -1 };
    const heads = inHand.length === 2;
    h.sbP = heads ? btn : nextIn(inHand, btn);
    h.bbP = nextIn(inHand, h.sbP);
    if (ante) for (const p of inHand) {
        const a = Math.min(ante, h.s[p].stack);
        h.s[p].stack -= a; h.s[p].total += a;
        if (!h.s[p].stack) h.s[p].allin = true;
        if (a) h.log.push({ p, a: 'ante', amt: a, st: 0 });
    }
    post(h, h.sbP, sb, 'sb');
    post(h, h.bbP, bb, 'bb');
    h.need = inHand.filter((p) => !h.s[p].allin);
    h.toAct = heads ? h.sbP : nextIn(inHand, h.bbP);
    settleTurn(h, true);
    return { ok: true, hand: h };
}

function post(h, p, amt, kind) {
    const x = h.s[p];
    const a = Math.min(amt, x.stack);
    x.stack -= a; x.bet += a; x.total += a;
    if (!x.stack) x.allin = true;
    h.log.push({ p, a: kind, amt: a, st: 0 });
}

function put(h, p, amt) {
    const x = h.s[p];
    x.stack -= amt; x.bet += amt; x.total += amt;
    if (!x.stack) x.allin = true;
}

const live = (h) => h.order.filter((p) => !h.s[p].fold);
const canBet = (h) => h.order.filter((p) => !h.s[p].fold && !h.s[p].allin);

/**
 * What the player to act may do.
 *   {fold, check, call, callAmt, raise, minTo, maxTo, allinTo}
 * `minTo`/`maxTo` are TOTAL bets for this round (what «raise to» means).
 */
export function options(h, pid) {
    const x = h && h.s[pid];
    if (!x || h.done || h.street === 'show' || h.toAct !== pid) return null;
    const toCall = Math.max(0, h.cur - x.bet);
    const others = canBet(h).filter((p) => p !== pid);
    const allinTo = x.bet + x.stack;
    const raise = x.stack > toCall && x.seq !== h.rseq && others.length > 0;
    const minTo = Math.min(h.cur + h.minR, allinTo);
    return {
        fold: true,
        check: toCall === 0,
        call: toCall > 0,
        callAmt: Math.min(toCall, x.stack),
        raise,
        bet: h.cur === 0,
        cur: h.cur,
        minTo,
        maxTo: allinTo,
        allinTo,
    };
}

/**
 * Apply an action of the player to act. a: 'fold' | 'check' | 'call' |
 * 'raise' (to = total bet this round; also a first bet) | 'allin'.
 * Mutates `h`. Returns {ok} or {ok:false, err, min?}.
 */
export function act(h, pid, a, to) {
    if (!h || h.done || h.street === 'show') return { ok: false, err: 'nohand' };
    if (h.toAct !== pid) return { ok: false, err: 'turn' };
    const x = h.s[pid];
    const toCall = Math.max(0, h.cur - x.bet);
    if (a === 'allin') {
        const t = x.bet + x.stack;
        if (t <= h.cur) a = 'call';
        else { a = 'raise'; to = t; }
    }
    switch (a) {
        case 'fold':
            x.fold = true;
            h.log.push({ p: pid, a: 'fold', st: h.street });
            break;
        case 'check':
            if (toCall > 0) return { ok: false, err: 'check' };
            x.seq = h.rseq;
            h.log.push({ p: pid, a: 'check', st: h.street });
            break;
        case 'call': {
            if (toCall === 0) { x.seq = h.rseq; h.log.push({ p: pid, a: 'check', st: h.street }); break; }
            const amt = Math.min(toCall, x.stack);
            put(h, pid, amt);
            x.seq = h.rseq;
            h.log.push({ p: pid, a: x.allin ? 'allin' : 'call', amt, st: h.street });
            break;
        }
        case 'raise': {
            const o = options(h, pid);
            to = Math.floor(+to);
            if (!o || !o.raise) return { ok: false, err: 'raise_closed' };
            if (!(to > h.cur)) return { ok: false, err: 'raise_low', min: o.minTo };
            if (to > o.maxTo) return { ok: false, err: 'raise_max', max: o.maxTo };
            const full = to - h.cur >= h.minR;
            if (!full && to !== o.maxTo) return { ok: false, err: 'raise_min', min: o.minTo };
            const wasBet = h.cur === 0;
            put(h, pid, to - x.bet);
            if (full) { h.minR = to - h.cur; h.rseq++; }
            h.cur = to;
            x.seq = h.rseq;
            // everybody still able to bet has to answer the new amount
            h.need = canBet(h).filter((p) => p !== pid);
            h.log.push({ p: pid, a: x.allin ? 'allin' : (wasBet ? 'bet' : 'raise'), amt: to, st: h.street, short: !full || undefined });
            break;
        }
        default:
            return { ok: false, err: 'action' };
    }
    h.need = h.need.filter((p) => p !== pid);
    settleTurn(h, false);
    return { ok: true };
}

/* After every action: end the hand, end the round, or pass the turn. */
function settleTurn(h, first) {
    const lv = live(h);
    if (lv.length === 1) { refundUncalled(h); finishFold(h, lv[0]); return; }
    h.need = h.need.filter((p) => !h.s[p].fold && !h.s[p].allin);
    // Nobody left to bet against: a player who owes nothing does not act.
    const bettors = canBet(h);
    h.need = h.need.filter((p) => h.cur - h.s[p].bet > 0 || bettors.some((q) => q !== p));
    if (!h.need.length) { endRound(h); return; }
    const from = first ? null : h.toAct;
    if (first && h.need.indexOf(h.toAct) >= 0) return;
    const start = from != null ? from : h.toAct;
    h.toAct = nextIn(h.order, start, (p) => h.need.indexOf(p) >= 0);
}

/* The part of the biggest bet nobody matched goes back to its owner. Folded
   players count: their chips were put in, they just stopped matching. */
function refundUncalled(h) {
    let top = null;
    for (const p of h.order) if (top === null || h.s[p].bet > h.s[top].bet) top = p;
    if (top === null) return;
    let second = 0;
    for (const p of h.order) if (p !== top) second = Math.max(second, h.s[p].bet);
    const extra = h.s[top].bet - second;
    if (extra > 0) {
        const x = h.s[top];
        x.bet -= extra; x.total -= extra; x.stack += extra;
        if (x.stack > 0) x.allin = false;
        h.refunds[top] = (h.refunds[top] || 0) + extra;
        h.log.push({ p: top, a: 'return', amt: extra, st: h.street });
    }
}

function endRound(h) {
    refundUncalled(h);
    const bettors = canBet(h);
    if (h.street === 3 || bettors.length < 2) {
        // river done, or nobody can bet any more: straight to the showdown
        h.runout = h.street < 3;
        h.street = 'show';
        h.toAct = null;
        h.need = [];
        for (const p of h.order) h.s[p].bet = 0;
        return;
    }
    h.street++;
    for (const p of h.order) { h.s[p].bet = 0; }
    h.cur = 0;
    h.minR = h.bb;
    h.rseq++;
    h.need = bettors.slice();
    h.toAct = nextIn(h.order, h.button, (p) => h.need.indexOf(p) >= 0);
}

function finishFold(h, winner) {
    h.done = true;
    h.street = 'show';
    h.toAct = null;
    h.need = [];
    h.winner = winner;
}

/* ── pots and the award ───────────────────────────────────────────────────── */

/**
 * The pots of the hand from each player's total contribution. Each entry:
 * {amount, elig:[pids]} — main pot first.
 */
export function pots(h) {
    if (!h) return [];
    const inPlay = live(h);
    const levels = Array.from(new Set(inPlay.map((p) => h.s[p].total).filter((t) => t > 0))).sort((a, b) => a - b);
    const out = [];
    let prev = 0;
    for (const L of levels) {
        let amount = 0;
        for (const p of h.order) amount += Math.max(0, Math.min(h.s[p].total, L) - prev);
        const elig = inPlay.filter((p) => h.s[p].total >= L);
        if (amount > 0) out.push({ amount, elig });
        prev = L;
    }
    // chips of folded players above every live level (only possible with
    // antes posted by a player who folded after being covered): dead money
    // for the last pot
    let dead = 0;
    for (const p of h.order) dead += Math.max(0, h.s[p].total - prev);
    if (dead > 0) {
        if (out.length) out[out.length - 1].amount += dead;
        else out.push({ amount: dead, elig: inPlay.slice() });
    }
    return out;
}

export function potTotal(h) {
    let s = 0;
    if (!h) return 0;
    for (const p of h.order) s += h.s[p].total;
    return s;
}

/**
 * Split the pots. winners: [[pid…] per pot] (a pot with one eligible player
 * needs no answer). unit — the smallest chip.
 * Returns {ok, deltas:{pid: n}, pots:[{amount, winners, shares:{pid:n}}]}.
 */
export function award(h, winners, unit) {
    if (!h) return { ok: false, err: 'nohand' };
    const u = unit > 0 ? unit : 1;
    const ps = pots(h);
    const deltas = {};
    for (const p of h.order) deltas[p] = -h.s[p].total;
    const result = [];
    // seat order starting left of the button — who gets the odd chips first
    const oddOrder = [];
    {
        let q = h.button;
        for (let k = 0; k < h.order.length; k++) { q = nextIn(h.order, q); oddOrder.push(q); }
    }
    for (let i = 0; i < ps.length; i++) {
        const pot = ps[i];
        let w;
        if (h.done && h.winner) w = [h.winner];
        else if (pot.elig.length === 1) w = pot.elig.slice();
        else {
            w = (winners && winners[i]) || [];
            w = Array.from(new Set(w));
            if (!w.length) return { ok: false, err: 'winner', pot: i };
            for (const p of w) if (pot.elig.indexOf(p) < 0) return { ok: false, err: 'elig', pot: i, pid: p };
        }
        w.sort((a, b) => oddOrder.indexOf(a) - oddOrder.indexOf(b));
        const units = Math.floor(pot.amount / u);
        const crumbs = pot.amount - units * u;       // less than one chip: first winner
        const each = Math.floor(units / w.length);
        let odd = units - each * w.length;
        const shares = {};
        for (const p of w) {
            let s = each * u;
            if (odd > 0) { s += u; odd--; }
            shares[p] = s;
        }
        shares[w[0]] += crumbs;
        for (const p of w) deltas[p] += shares[p];
        result.push({ amount: pot.amount, elig: pot.elig.slice(), winners: w, shares });
    }
    return { ok: true, deltas, pots: result };
}

/** Big blind, small blind and button markers for a seat. */
export function roles(h, pid) {
    if (!h) return [];
    const r = [];
    if (h.button === pid) r.push('D');
    if (h.sbP === pid) r.push('SB');
    if (h.bbP === pid) r.push('BB');
    return r;
}
